import { Directive, DestroyRef, ElementRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Select, SelectChangeEvent } from 'primeng/select';

/**
 * Makes the hover-gated clear ("X") icon behave on filing-form dropdowns:
 * **picking a value hides the X; hovering the dropdown brings it back to click.**
 *
 * Applies to EVERY `<p-select>` in a component that imports it — no attribute
 * needed. That is deliberate: the behaviour needs two things done together, and
 * every previous bug here came from doing one and forgetting the other. Import
 * the directive and both are guaranteed.
 *
 * ## The two things, and why both are required
 *
 * The gate in `src/styles.css` hides the icon only when all four of these are
 * false:
 *
 *   .p-select:not(:hover):not(:focus-within):not(.p-focus):not(.p-select-open)
 *
 * A mouse pick leaves TWO true, for unrelated reasons:
 *
 * 1. **Focus** — PrimeNG re-focuses the trigger when the panel closes
 *    (`Select.onOptionSelect` -> `hide(true)` -> `focus(focusInputViewChild)`),
 *    setting `.p-focus`. → answered by the deferred blur below. Measured:
 *    without it `p-focus=true` after a pick, with it `false`.
 * 2. **Hover** — with no `appendTo`, PrimeNG renders the panel AND every
 *    `<li role="option">` as a DOM DESCENDANT of `<p-select>`. CSS `:hover`
 *    matches an element when the pointer is over it *or any descendant*, so at
 *    the instant of the click `.p-select:hover` is TRUE, and Chrome only
 *    re-evaluates hover on pointer MOVEMENT — not on the panel's removal.
 *    → answered by forcing `appendTo = 'body'` below, which lifts the panel out
 *    of the host so it can never contribute to the trigger's hover state.
 *
 * This is not theoretical. The three MSIC "Principal Activities" dropdowns on
 * Scope of Filing were the only ones that already set `appendTo`, and they were
 * the only ones that behaved correctly while the other twenty did not. **Three
 * attempts to solve half 2 without `appendTo`** — a CSS-only gate tweak, an
 * imperative suppression class, then that class as a host binding — **all
 * failed.** Remove the difference; do not compensate for it.
 *
 * ## Why `appendTo` is set here rather than in 23 templates
 *
 * `Select.appendTo` is a plain input with exactly ONE consumer — its own
 * `<p-overlay [appendTo]="appendTo">`. Setting it in this constructor lands
 * before that view is created, and because Angular applies template inputs
 * *after* construction, an explicit `[appendTo]` on an individual dropdown still
 * wins if a local override is ever needed.
 *
 * The trade-off, stated plainly: the panel's placement is no longer visible in
 * the template. That is the price of making it impossible to forget.
 *
 * ## The guards are the design
 *
 * - **Value gate** — `Select.clear()` also emits `onChange`, with a null value;
 *   the clear button's own behaviour must not change.
 * - **Pointer gate** — a keyboard pick keeps its focus. Blurring there would
 *   send focus to `<body>`, reset the tab order, and kill the `Delete`-to-clear
 *   shortcut `[showClear]` enables. `MouseEvent` covers `PointerEvent` (it
 *   extends it); anything not provably a pointer is left alone.
 * - **Deferral** — `onChange` is emitted BEFORE `hide(true)` re-focuses, so a
 *   synchronous blur is silently undone. The microtask runs after the whole
 *   dispatch, including `onContainerClick`'s own `focus()`.
 *
 * Nothing here writes form data — no value, validity, or touched/dirty change.
 *
 * Cover: `select-clear-icon-behavior.directive.spec.ts`. **A Karma
 * `element.click()` carries no pointer position, so `:hover` can never be true
 * in the harness, and global stylesheets are not loaded — a green run does NOT
 * prove the icon is hidden on screen.** The spec asserts the two mechanisms
 * instead (focus left the host; the panel renders outside the host). Verify the
 * visual result with a real mouse.
 */
@Directive({
  selector: 'p-select',
  standalone: true,
})
export class SelectClearIconBehaviorDirective {
  private readonly select = inject(Select, { self: true });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Half 2 — keep the open panel out of the trigger's :hover chain.
    // A template-level [appendTo] still overrides this (inputs are applied
    // after construction), so per-dropdown opt-out remains possible.
    this.select.appendTo = 'body';

    // Half 1 — drop the focus PrimeNG restores when the panel closes.
    this.select.onChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: SelectChangeEvent) => {
        if (event.value == null) return;
        if (!(event.originalEvent instanceof MouseEvent)) return;

        queueMicrotask(() => this.blurWithinHost());
      });
  }

  /**
   * Blur whatever holds focus, but only if it lives inside this dropdown.
   *
   * Reading `document.activeElement` rather than PrimeNG's `focusInputViewChild`
   * keeps this off an undocumented ViewChild, and the `contains` check means the
   * directive can never blur an element some other handler legitimately focused
   * elsewhere in the meantime.
   */
  private blurWithinHost(): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && this.host.nativeElement.contains(active)) {
      active.blur();
    }
  }
}
